import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import { Loader2, AlertCircle, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { capture as posthogCapture } from "@/services/posthog";
import { trackMetaEvent } from "@/services/analytics";

const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || ""
);

const CheckoutPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const clientSecret = searchParams.get("clientSecret");
  const eventId = searchParams.get("eventId");
  const returnTo = searchParams.get("returnTo") || (eventId ? `/events/${eventId}` : "/events");

  useEffect(() => {
    if (!clientSecret) return;
    // Infer checkout type from returnTo — cheap heuristic without changing callers.
    let checkoutType: string = "ticket";
    if (returnTo.startsWith("/profile")) checkoutType = "credit_pack";
    if (returnTo.startsWith("/offers")) checkoutType = "campaign_package";
    posthogCapture("checkout_opened", {
      checkout_type: checkoutType,
      event_id: eventId ?? undefined,
    });
    if (checkoutType === "ticket" && eventId) {
      trackMetaEvent("InitiateCheckout", {
        content_ids: [eventId],
        content_type: "product",
        content_category: "event",
      });
    }
  }, [clientSecret, returnTo, eventId]);

  if (!clientSecret) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 text-center space-y-4">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
          <h1 className="text-xl font-bold text-foreground">
            Checkout Unavailable
          </h1>
          <p className="text-muted-foreground">
            Something went wrong loading the checkout. Please try booking again.
          </p>
          <Button
            onClick={() => navigate(returnTo)}
            className="w-full"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="border-b border-border bg-card px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(returnTo)}
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <h1 className="text-lg font-semibold text-foreground">
            Complete Payment
          </h1>
        </div>
      </div>

      {/* Checkout form */}
      <div className="flex-1 flex items-start justify-center p-4 pt-8">
        <div className="w-full max-w-lg">
          <EmbeddedCheckoutProvider
            stripe={stripePromise}
            options={{ clientSecret }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
